import { withoutInternalOnlyOnCustomerWork } from '../utils/customerWorkPeople';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Paperclip, Image, Mic, Volume2, Loader2 } from 'lucide-react';
import { toast } from '../store/toastStore';
import { fileDropHandlers, splitBySize } from '../utils/attachPasteDrop';
import { useUploadProgress } from '../utils/useUploadProgress';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { usePermissions } from '../hooks/usePermissions';
import { getPriorityLabels } from '../types';
import type { Ticket, TicketPriority, TaskType } from '../types';
import VoiceRecorder from './VoiceRecorder';
import CameraCapture from './CameraCapture';
import { createPreviewPlaybackUrl } from '../utils/audioRecording';
import Dialog from './ui/Dialog';
import Field, { FormErrorSummary, FormServerError, rules, useFormValidation, type FormSchema } from './ui/Field';
import EngagementChainFields, { LockedChainStrip, useEngagementChain, useResolvedPlacement } from './engagements/EngagementChainFields';
import { EMPTY_CHAIN, type ChainValue } from './engagements/engagementChainModel';
import { isProjectEngagementType } from '../utils/engagementLabel';
import { STAFF_TICKET_SOURCES } from '../utils/ticketStatus.shared';
import EntityPicker from './ui/EntityPicker';
import DateInput from './ui/DateInput';

/**
 * "קריאה חדשה": the one create-ticket form. The tickets page opens it (its
 * "+" and `?create=true`), and so does QuickCreate (the floating button and
 * `N`), in place over the current screen with the screen's context filled in
 * (DESIGN_SYSTEM.md §10.4). Moved here from `pages/TicketsPage.tsx`.
 *
 * P-02b (T-02): a service ticket is opened on an ENGAGEMENT, not a project.
 * Staff pick the customer (active only, D-30) and then one of its
 * engagements (any of the 5 types, "name · type"). There is no project
 * picker: on a project / development the engagement's projects row is sent
 * with it.
 */

const ATTACH_ACCEPT = 'image/jpeg,image/png,image/gif,image/webp,application/pdf,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,' +
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/zip';
const CREATE_TICKET_FORM_ID = 'create-ticket-form';
/** Create-ticket validation (T-43): title 3–500; staff: customer + engagement (T-02). */
const CREATE_TICKET_SCHEMA: FormSchema = {
  'ct-title': [rules.required(), rules.minLength(3), rules.maxLength(500)],
};
/**
 * D-146 (Q-98) — a service ticket is opened WITHOUT an engagement (T-02 at
 * opening is cancelled); it is asked on "התחלת עבודה".
 */
const CREATE_TICKET_SCHEMA_INTERNAL_TICKET: FormSchema = {
  'ct-customer': [rules.required('engagementChain.errors.customerRequired')],
  ...CREATE_TICKET_SCHEMA,
};

function FilePreviewChip({ file, onRemove }: { file: File; onRemove: () => void }) {
  const { t } = useTranslation();
  const [url, setUrl] = useState<string>('');
  const isImg = file.type.startsWith('image/');
  const isAudio = file.type.startsWith('audio/');
  useEffect(() => {
    if (!isImg && !isAudio) return;
    let revoked = false;
    let objectUrl = '';
    const load = async () => {
      try {
        objectUrl = isAudio ? await createPreviewPlaybackUrl(file) : URL.createObjectURL(file);
        if (!revoked) setUrl(objectUrl);
        else URL.revokeObjectURL(objectUrl);
      } catch {
        objectUrl = URL.createObjectURL(file);
        if (!revoked) setUrl(objectUrl);
      }
    };
    void load();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [file, isImg, isAudio]);

  if (isAudio && url) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 bg-surface-50 rounded-lg border border-surface-200 text-xs text-surface-800 relative min-w-36">
        <Volume2 size={14} className="shrink-0 text-surface-500" aria-hidden="true" />
        {/* Preview of an audio file the user is attaching: no caption file exists. */}
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio src={url} controls className="h-9 min-w-36 max-w-52" preload="auto" playsInline />
        <button
          type="button"
          onClick={onRemove}
          className="p-1 rounded-xs text-surface-500 hover:text-danger-700 hover:bg-danger-50 shrink-0"
          aria-label={`${t('common.remove')}: ${file.name}`}
        >
          <X size={14} aria-hidden="true" />
        </button>
      </div>
    );
  }

  if (isImg && url) {
    return (
      <div className="relative group">
        <img src={url} alt={file.name} className="w-20 h-20 rounded-lg object-cover border border-surface-200" />
        <button type="button" onClick={onRemove} aria-label={`${t('common.remove')}: ${file.name}`}
          className="absolute -top-2 -end-2 w-6 h-6 bg-surface-0 text-surface-700 border border-surface-200 rounded-full flex items-center justify-center shadow-elevated hover:text-danger-700">
          <X size={12} aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 ps-2 pe-1 py-1 bg-surface-100 rounded-lg text-xs text-surface-700">
      <Paperclip size={12} aria-hidden="true" />
      <span className="max-w-36 truncate" dir="auto">{file.name}</span>
      <button type="button" onClick={onRemove} aria-label={`${t('common.remove')}: ${file.name}`}
        className="p-1 rounded-xs text-surface-500 hover:text-danger-700 hover:bg-danger-50"><X size={12} aria-hidden="true" /></button>
    </div>
  );
}

export interface CreateTicketInput {
  title: string; description: string; priority: TicketPriority; task_type: TaskType; files: File[];
  customer_id?: string; assigned_agent_id?: string; contact_id?: string; due_date?: string;
  project_id?: string;
  /** Optional for a ticket (D-146, asked on "התחלת עבודה"). */
  engagement_id?: string;
  /** D-146 — the customer of a ticket opened without an engagement. */
  organization_id?: string;
  /** D-146 (Q-102) — phone / email / whatsapp / meeting. */
  source?: string;
  extra_customer_ids?: string[]; additional_agent_ids?: string[];
}

/**
 * The create-ticket request (JSON, or multipart when files are attached) and
 * the list refresh. `onCreated` gets the new ticket when the server returns it.
 */
export function useCreateTicket(onCreated?: (ticket: Ticket | undefined) => void) {
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: (d: CreateTicketInput) => {
      const payload = { ...d, customer_id: d.customer_id || user!.id };
      return d.files.length > 0
        ? api.createTicketWithFiles(payload, d.files)
        : api.createTicket(payload);
    },
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      onCreated?.(res?.data);
    },
  });
}

export default function CreateTicketModal({ onClose, onSubmit, loading, error, organizationId, initialProjectId, initialEngagementId }: {
  /** When set, the customer is fixed (locked) and only its users are offered as the requester. */
  organizationId?: string;
  /** Pre-select the engagement of this project (QuickCreate context). */
  initialProjectId?: string;
  /** Pre-select this engagement (QuickCreate context). */
  initialEngagementId?: string;
  onClose: () => void;
  onSubmit: (data: CreateTicketInput) => void;
  loading: boolean;
  error: unknown;
}) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isInternal = user?.role === 'admin' || user?.role === 'agent';
  // P-33c (D-185): internal-only — on "עבודה פנימית"; no customer, engagement, requester, type or other assignee.
  const { internalOnly } = usePermissions();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  // P-36 (D-193): this form opens a service ticket only; a discussion has its own form (NewDiscussionModal).
  const newTicketType = 'ticket' as const;
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [assignedAgentId, setAssignedAgentId] = useState('');
  const [dueDate, setDueDate] = useState('');
  // D-146 (Q-102) — the channel the customer used (an employee opens on their behalf).
  const [source, setSource] = useState('');
  // T-02 — customer → engagement (the project follows the engagement).
  const placement = useResolvedPlacement(isInternal && (organizationId || initialProjectId || initialEngagementId)
    ? { organizationId, projectId: initialProjectId, engagementId: initialEngagementId }
    : undefined);
  const [chainValue, setChainValue] = useState<ChainValue>(EMPTY_CHAIN);
  const [placed, setPlaced] = useState(false);
  if (placement.ready && !placed) {
    setPlaced(true);
    setChainValue({ ...placement.value, engagementType: '', milestoneId: '' });
  }
  const chain = useEngagementChain({
    value: chainValue,
    onChange: setChainValue,
    lock: organizationId ? 'customer' : undefined,
    withType: false,
    withMilestone: false,
    anyEngagement: true,
    enabled: isInternal && placement.ready,
  });
  const ticketOrgId = chainValue.organizationId;
  const fileRef = useRef<HTMLInputElement>(null);
  const galleryRef = useRef<HTMLInputElement>(null);
  const [voiceRecorderOpen, setVoiceRecorderOpen] = useState(false);

  const { data: usersData } = useQuery({
    queryKey: ['users-for-create'],
    queryFn: () => api.getUsers(),
    enabled: isInternal && !internalOnly,
  });
  // P-02c/F-01 — a new ticket is not opened for an archived (inactive)
  // customer: its users are left out of the requester list (unless the form
  // was opened for that customer). Excluding the inactive ones never hides an
  // active customer the organizations scope happens not to list.
  const { data: inactiveOrgsData } = useQuery({
    queryKey: ['organizations', 'inactive-for-create'],
    queryFn: () => api.getOrganizations({ status: 'inactive', limit: 500 }),
    enabled: isInternal && !organizationId && !internalOnly,
    staleTime: 60_000,
  });
  const inactiveOrgIds = new Set((inactiveOrgsData?.data ?? []).map((o: { id: string }) => o.id));
  const users = usersData?.data || [];
  // The requester belongs to the chosen customer (none chosen yet: any active one).
  const customers = users.filter((u: any) =>
    (u.role === 'customer_viewer' || u.role === 'customer_manager') &&
    (ticketOrgId ? u.organization_id === ticketOrgId
      : organizationId ? u.organization_id === organizationId : !inactiveOrgIds.has(u.organization_id)));
  // D-196: with a customer chosen an internal-only employee is not offered as the owner.
  const agents = withoutInternalOnlyOnCustomerWork(
    users.filter((u: any) => u.role === 'admin' || u.role === 'agent'), !!(ticketOrgId || organizationId));

  // Another customer: a requester of the old one is dropped.
  useEffect(() => {
    if (!customerId || !ticketOrgId) return;
    const req = users.find((u: any) => u.id === customerId);
    if (req && req.organization_id !== ticketOrgId) setCustomerId('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticketOrgId]);

  // R-02: one path for picking, pasting, dropping, camera and voice; a file that is too big is named, never dropped silently.
  const addPicked = (picked: File[]) => {
    const { ok, tooBig } = splitBySize(picked);
    tooBig.forEach((b) => toast('error', t('media.tooLargeNamed', b)));
    setFileError(tooBig.map((b) => t('media.tooLargeNamed', b)).join(' · '));
    if (ok.length) setFiles(prev => [...prev, ...ok]);
  };
  const addFilesFromInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    addPicked(Array.from(e.target.files || []));
    e.target.value = '';
  };
  const drop = fileDropHandlers(addPicked);
  const uploadPct = useUploadProgress(loading && files.length > 0);

  const schema = !isInternal ? CREATE_TICKET_SCHEMA : CREATE_TICKET_SCHEMA_INTERNAL_TICKET;
  const v = useFormValidation(schema, { 'ct-title': title, 'ct-customer': chainValue.organizationId, 'ct-engagement': chainValue.engagementId });
  const fieldLabels = { 'ct-title': t('common.title'), 'ct-customer': t('engagementChain.customer'), 'ct-engagement': t('engagementChain.engagement') };
  const selectedEngagement = chain.selectedEngagement;

  const handleSubmit = v.submit(() => {
    if (loading) return; // R-02c: Enter in a field must not send a second ticket while the first one uploads
    onSubmit({
      title, description, priority, task_type: newTicketType, files,
      customer_id: customerId || undefined,
      assigned_agent_id: assignedAgentId || undefined,
      due_date: dueDate || undefined,
      engagement_id: isInternal ? (chainValue.engagementId || undefined) : undefined,
      // D-146 — the customer, for a ticket opened without an engagement.
      organization_id: isInternal ? (chainValue.organizationId || undefined) : undefined,
      source: isInternal ? (source || undefined) : undefined,
      // On a project / development the engagement's projects row goes with it (T-02).
      project_id: isInternal && selectedEngagement && isProjectEngagementType(selectedEngagement.type)
        ? (selectedEngagement.project_id || undefined) : undefined,
    });
  });

  const attachTile = 'flex min-h-touch flex-col items-center gap-1.5 rounded-lg border border-dashed border-control px-2 py-3 text-text-2 transition-colors duration-micro hover:bg-surface-subtle hover:text-text';

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('tickets.newTicket')}
      size="lg"
      dismissible={!loading}
      footer={(
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary flex-1 sm:flex-none">{t('common.cancel')}</button>
          <button
            type="submit"
            form={CREATE_TICKET_FORM_ID}
            disabled={loading}
            aria-busy={loading || undefined}
            className="btn btn-primary flex-1 sm:flex-none sm:min-w-36"
          >
            {loading && <Loader2 size={16} className="animate-spin" aria-hidden="true" />}
            {t('tickets.createTicket')}
          </button>
        </>
      )}
    >
        <form id={CREATE_TICKET_FORM_ID} onSubmit={handleSubmit} noValidate>
          {/* Enter outside a textarea does not submit (keys bubble here from the fields). */}
          {/* D-316 — compact: the locked customer in one line on top, short fields two in a row. */}
          <div
            role="presentation"
            className="space-y-3"
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
                e.preventDefault();
              }
            }}
          >
            {v.showSummary && <FormErrorSummary errors={v.errors} labels={fieldLabels} order={Object.keys(schema)} />}
            {isInternal && <LockedChainStrip chain={chain} />}
            <Field id="ct-title" label={t('common.title')} required error={v.errors['ct-title']}>
              <input className="input" value={title} onChange={(e) => setTitle(e.target.value)} onBlur={() => v.blur('ct-title')} maxLength={500} autoComplete="off" />
            </Field>
            <Field id="ct-description" label={t('common.description')}>
              <textarea className="input min-h-20 resize-y" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} {...drop} />
            </Field>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Field id="ct-priority" label={t('common.priority')}>
                <select className="input" value={priority} onChange={(e) => setPriority(e.target.value as TicketPriority)}>
                  {Object.entries(getPriorityLabels(t)).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </Field>
            </div>

            {/* A customer manager does not choose who handles the request (owner, fix round
                2026-10-07): the field is gone and OTE assigns it as for any customer request. */}
            {/* Extended fields for internal roles */}
            {isInternal && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <EngagementChainFields chain={chain} idPrefix="ct" errors={v.errors} onBlurField={(id) => v.blur(id)} hideLocked />
                <Field id="ct-source" label={t('ticketSource.label')} hint={t('ticketSource.hint')}>
                  <select className="input" value={source} onChange={(e) => setSource(e.target.value)}>
                    <option value="">{t('ticketSource.none')}</option>
                    {STAFF_TICKET_SOURCES.map((s) => <option key={s} value={s}>{t(`ticketSource.${s}`)}</option>)}
                  </select>
                </Field>
                <Field id="ct-due" label={t('tickets.dueDate')}>
                  <DateInput className="input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
                </Field>
                {!internalOnly && (
                  <Field id="ct-requester" label={t('uxTickets.requester')}>
                    <EntityPicker
                      id="ct-requester"
                      placeholder={t('tickets.selectCustomer')}
                      emptyLabel={t('tickets.selectCustomer')}
                      options={customers.map((u: any) => ({ value: u.id, label: u.name, secondary: u.email }))}
                      value={customerId}
                      onChange={setCustomerId}
                    />
                  </Field>
                )}
                {!internalOnly && (
                  <Field id="ct-agent" label={t('tickets.assignAgent')}>
                    <EntityPicker
                      id="ct-agent"
                      emptyLabel={t('tickets.autoAssign')}
                      options={agents.map((u: any) => ({ value: u.id, label: u.name }))}
                      value={assignedAgentId}
                      onChange={setAssignedAgentId}
                    />
                  </Field>
                )}
              </div>
            )}

            <FormServerError error={error} fallback={t('tickets.createError')} />
          </div>
        </form>

        {/* Attachments + voice: outside the form so the mic never submits the ticket */}
        <section className="mt-4 space-y-2" aria-labelledby="ct-files-title" onDragOver={drop.onDragOver} onDrop={drop.onDrop}>
          <h3 id="ct-files-title" className="label">
            {t('tickets.files')}
          </h3>
          {fileError && <p role="alert" className="text-xs text-danger-700">{fileError}</p>}
          {uploadPct !== null && <p role="status" className="text-xs text-text-2">{t('media.createUploading', { pct: uploadPct })}</p>}
          {files.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {files.map((f, i) => (
                <FilePreviewChip key={i} file={f} onRemove={() => setFiles(prev => prev.filter((_, j) => j !== i))} />
              ))}
            </div>
          )}

          {voiceRecorderOpen ? (
            <div className="space-y-2">
              <p className="text-xs text-text-2">{t('voice.savedAsAttachment')}</p>
              <VoiceRecorder
                fullWidth
                autoStart
                confirmBeforeSend
                onRecordingComplete={(file) => {
                  addPicked([file]);
                  setVoiceRecorderOpen(false);
                }}
                onCancel={() => setVoiceRecorderOpen(false)}
              />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <button type="button" onClick={() => galleryRef.current?.click()} className={attachTile}>
                <Image size={18} strokeWidth={1.75} aria-hidden="true" />
                <span className="text-xs">{t('tickets.gallery')}</span>
              </button>
              <CameraCapture
                variant="grid"
                onCapture={(file) => addPicked([file])}
              />
              <button type="button" onClick={() => fileRef.current?.click()} className={attachTile}>
                <Paperclip size={18} strokeWidth={1.75} aria-hidden="true" />
                <span className="text-xs">{t('tickets.addFile')}</span>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setVoiceRecorderOpen(true);
                }}
                className={attachTile}
              >
                <Mic size={18} strokeWidth={1.75} aria-hidden="true" />
                <span className="text-xs">{t('voice.record')}</span>
              </button>
            </div>
          )}

          <input ref={fileRef} type="file" accept={ATTACH_ACCEPT + ',audio/webm,audio/ogg,audio/mpeg,video/mp4'} multiple className="hidden" onChange={addFilesFromInput} />
          <input ref={galleryRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple className="hidden" onChange={addFilesFromInput} />
        </section>
    </Dialog>
  );
}
